import { useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  BarChart3,
  Check,
  Image as ImageIcon,
  Megaphone,
  MousePointerClick,
  Upload,
} from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Field, Input, Select, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'
import {
  useBuyPromo,
  useMyPromos,
  usePromoPackages,
  useUploadMyPromoImage,
  type AdminPromo,
  type PromoAccent,
  type PromoPackage,
} from '@/features/promo/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatRupiah } from '@/lib/format'

export function PromosiSayaPage() {
  const { data, isLoading } = useMyPromos()
  const promos = data?.promos ?? []
  const [beli, setBeli] = useState(false)

  const total = promos.reduce(
    (acc, p) => ({
      tayang: acc.tayang + p.impressions,
      klik: acc.klik + p.clicks,
      biaya: acc.biaya + p.price,
    }),
    { tayang: 0, klik: 0, biaya: 0 },
  )

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Promosi saya</h1>
          <p className="mt-1 text-muted">
            Pasang karya atau penawaran Anda di headline beranda skava.id, dilihat semua pengunjung.
          </p>
        </div>
        <Button onClick={() => setBeli(true)}>
          <Megaphone className="size-4" />
          Pasang promosi
        </Button>
      </header>

      {promos.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-3">
          <Angka label="Total tayang" nilai={total.tayang.toLocaleString('id-ID')} icon={<BarChart3 className="size-4" />} />
          <Angka
            label="Total klik"
            nilai={`${total.klik.toLocaleString('id-ID')} · ${
              total.tayang === 0 ? '0,0' : ((total.klik / total.tayang) * 100).toFixed(1)
            }%`}
            icon={<MousePointerClick className="size-4" />}
          />
          <Angka label="Total dibayar" nilai={formatRupiah(total.biaya)} icon={<ImageIcon className="size-4" />} />
        </div>
      ) : null}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : promos.length === 0 ? (
        <Card>
          <EmptyState
            illustration="box"
            icon={<Megaphone className="size-6" />}
            title="Belum ada promosi"
            description="Petak headline beranda dilihat setiap pengunjung skava.id, termasuk yang belum punya akun. Mulai dari Rp 150.000 untuk tujuh hari."
            action={<Button onClick={() => setBeli(true)}>Lihat paketnya</Button>}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {promos.map((p) => (
            <BarisPromosi key={p.id} promo={p} />
          ))}
        </div>
      )}

      <DialogBeli open={beli} onClose={() => setBeli(false)} />
    </div>
  )
}

function Angka({ label, nilai, icon }: { label: string; nilai: string; icon: React.ReactNode }) {
  return (
    <Card>
      <CardBody className="space-y-1">
        <p className="mono-label flex items-center gap-1.5 text-[11px] text-subtle">
          {icon}
          {label}
        </p>
        <p className="text-xl font-extrabold tabular-nums text-ink">{nilai}</p>
      </CardBody>
    </Card>
  )
}

function BarisPromosi({ promo }: { promo: AdminPromo }) {
  const menunggu = promo.status === 'draft'

  return (
    <Card>
      <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="h-20 w-full shrink-0 overflow-hidden rounded-xl border border-line bg-sunken sm:w-32">
          {promo.image_url ? (
            <img src={promo.image_url} alt="" className="size-full object-cover" />
          ) : (
            <div className="grid size-full place-items-center text-subtle">
              <ImageIcon className="size-5" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={promo.live ? 'success' : menunggu ? 'warning' : 'neutral'}>
              {promo.live ? 'Sedang tayang' : menunggu ? 'Menunggu pembayaran' : 'Selesai'}
            </Badge>
            <span className="mono-label text-[10px] text-subtle">{formatRupiah(promo.price)}</span>
          </div>

          <p className="mt-1.5 truncate font-bold text-ink">{promo.title}</p>
          {promo.subtitle ? <p className="truncate text-sm text-muted">{promo.subtitle}</p> : null}

          <p className="mono-label mt-1.5 text-[11px] text-subtle">
            {promo.impressions.toLocaleString('id-ID')} tayang · {promo.clicks.toLocaleString('id-ID')} klik ·{' '}
            {promo.click_rate.toFixed(1)}%
          </p>

          {promo.starts_at && promo.ends_at ? (
            <p className="mt-1 text-xs text-subtle">
              Tayang {formatDate(promo.starts_at)} sampai {formatDate(promo.ends_at)}
            </p>
          ) : null}
        </div>

        {menunggu ? (
          <Link to="/app/pesanan">
            <Button size="sm">Selesaikan pembayaran</Button>
          </Link>
        ) : null}
      </CardBody>
    </Card>
  )
}

function DialogBeli({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data } = usePromoPackages()
  const paket = data?.packages ?? []

  const [dipilih, setDipilih] = useState('')
  const [judul, setJudul] = useState('')
  const [keterangan, setKeterangan] = useState('')
  const [gambar, setGambar] = useState('')
  const [tombol, setTombol] = useState('')
  const [tautan, setTautan] = useState('')
  const [warna, setWarna] = useState<PromoAccent>('hijau')
  const [galat, setGalat] = useState('')

  const berkas = useRef<HTMLInputElement>(null)
  const unggah = useUploadMyPromoImage()
  const beli = useBuyPromo()
  const toast = useToast()

  const kirim = () => {
    setGalat('')

    if (!gambar) {
      setGalat('Gambar wajib diunggah. Petak headline tanpa gambar hanya menyisakan ruang kosong di beranda.')
      return
    }

    beli.mutate(
      {
        package: dipilih || paket.find((p) => p.highlighted)?.code || paket[0]?.code || '',
        title: judul,
        subtitle: keterangan,
        image_url: gambar,
        cta_label: tombol,
        target_url: tautan,
        accent: warna,
      },
      {
        onSuccess: (hasil) => {
          // Pembeli langsung dibawa ke halaman pembayaran Midtrans. Menunda
          // ke halaman pesanan menambah satu langkah yang tidak menambah
          // kejelasan apa pun.
          if (hasil.pay_url) {
            window.location.href = hasil.pay_url
            return
          }
          toast.success('Promosi dibuat', 'Selesaikan pembayarannya di halaman Pesanan.')
          onClose()
        },
        onError: (error) => setGalat(error instanceof ApiError ? error.message : 'Gagal membuat promosi.'),
      },
    )
  }

  const terpilih = dipilih || paket.find((p) => p.highlighted)?.code

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Pasang promosi di headline"
      description="Tampil di petak paling atas beranda skava.id, dilihat semua pengunjung termasuk yang belum punya akun."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Batal
          </Button>
          <Button onClick={kirim} disabled={beli.isPending}>
            {beli.isPending ? 'Menyiapkan…' : 'Lanjut ke pembayaran'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {galat ? (
          <p className="rounded-xl bg-danger-soft px-3 py-2 text-sm font-medium text-danger" role="alert">
            {galat}
          </p>
        ) : null}

        <Field label="Lama tayang" required>
          <div className="grid gap-3 sm:grid-cols-3">
            {paket.map((p) => (
              <KartuPaket
                key={p.code}
                paket={p}
                terpilih={terpilih === p.code}
                onPilih={() => setDipilih(p.code)}
              />
            ))}
          </div>
        </Field>

        <Input
          label="Judul"
          required
          value={judul}
          onChange={(e) => setJudul(e.target.value)}
          placeholder="Jasa pembuatan aplikasi mobile"
          hint="Ini yang terbaca paling besar. Sebutkan yang Anda tawarkan, bukan nama usaha saja."
        />

        <Textarea
          label="Keterangan"
          rows={2}
          value={keterangan}
          onChange={(e) => setKeterangan(e.target.value)}
          placeholder="Android dan iOS, harga dan lama pengerjaan tertulis sejak awal."
        />

        <Field label="Gambar" required hint="JPG, PNG, atau WebP, maksimal 3 MB. Ukuran ideal 1600×900.">
          <div className="flex items-center gap-3">
            <div className="h-20 w-32 shrink-0 overflow-hidden rounded-xl border border-line bg-sunken">
              {gambar ? (
                <img src={gambar} alt="" className="size-full object-cover" />
              ) : (
                <div className="grid size-full place-items-center text-subtle">
                  <ImageIcon className="size-5" />
                </div>
              )}
            </div>
            <div>
              <input
                ref={berkas}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  unggah.mutate(file, {
                    onSuccess: (hasil) => setGambar(hasil.image_url),
                    onError: (error) =>
                      toast.error('Gambar gagal diunggah', error instanceof Error ? error.message : undefined),
                  })
                }}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => berkas.current?.click()}
                disabled={unggah.isPending}
              >
                <Upload className="size-4" />
                {unggah.isPending ? 'Mengunggah…' : gambar ? 'Ganti gambar' : 'Pilih gambar'}
              </Button>
            </div>
          </div>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Teks tombol"
            value={tombol}
            onChange={(e) => setTombol(e.target.value)}
            placeholder="Lihat penawaran"
          />
          <Input
            label="Tautan tujuan"
            value={tautan}
            onChange={(e) => setTautan(e.target.value)}
            placeholder="/jasa atau https://…"
            hint="Diawali / untuk halaman di dalam skava.id."
          />
        </div>

        <Select label="Warna aksen" value={warna} onChange={(e) => setWarna(e.target.value as PromoAccent)}>
          <option value="hijau">Hijau</option>
          <option value="ungu">Ungu</option>
          <option value="kuning">Kuning</option>
          <option value="gelap">Gelap</option>
        </Select>

        <p className="rounded-xl bg-sunken px-3 py-2.5 text-sm text-muted">
          Promosi mulai tayang begitu pembayaran terkonfirmasi, dan masa tayangnya dihitung sejak saat itu —
          bukan sejak Anda memesannya. Tim kami dapat menghentikan promosi yang isinya menyesatkan.
        </p>
      </div>
    </Dialog>
  )
}

function KartuPaket({
  paket,
  terpilih,
  onPilih,
}: {
  paket: PromoPackage
  terpilih: boolean
  onPilih: () => void
}) {
  return (
    <button
      type="button"
      onClick={onPilih}
      aria-pressed={terpilih}
      className={`relative rounded-2xl border-2 p-4 text-left transition ${
        terpilih ? 'border-brand bg-brand-soft' : 'border-line hover:border-ink-subtle'
      }`}
    >
      {paket.highlighted ? (
        <span className="mono-label absolute -top-2.5 left-4 rounded-full bg-kuning-500 px-2 py-0.5 text-[9px] text-arang-900">
          Paling dipilih
        </span>
      ) : null}

      <div className="flex items-center justify-between">
        <p className="font-extrabold text-ink">{paket.name}</p>
        {terpilih ? <Check className="size-4 text-brand" /> : null}
      </div>
      <p className="mt-1 text-lg font-extrabold tabular-nums text-ink">{formatRupiah(paket.price)}</p>
      <p className="mt-1.5 text-xs leading-relaxed text-muted">{paket.summary}</p>
    </button>
  )
}
